import React, { useCallback, useEffect, useState } from "react";
import { packsAPI, productsAPI, variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * Bundles sold at one price.
 *
 * A pack has no stock of its own — how many you can sell is decided by
 * whichever component runs out first, which is what the availability figure
 * here is telling the owner.
 *
 * A component is a product, or a **range** ("any variant of this"): the cashier
 * picks which one the customer wants when the pack is sold.
 */
const Packs = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);
  const [packs, setPacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, pr, rg] = await Promise.all([
        packsAPI.getAll(),
        productsAPI.getAll(),
        // A member who may edit packs but not browse ranges simply sees none
        variantGroupsAPI.list().catch(() => ({ data: [] })),
      ]);
      setPacks(p.data);
      setProducts(pr.data);
      setRanges(rg.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("packs.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const listValue = (items) =>
    items.reduce((sum, item) => {
      const priced = item.variant_group_id
        ? ranges.find((r) => r.id === item.variant_group_id)
        : products.find((p) => p.id === item.product_id);
      return sum + (priced ? Number(priced.price) * item.quantity : 0);
    }, 0);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    const payload = {
      name: editing.name,
      description: editing.description || null,
      price: parseFloat(editing.price),
      items: editing.items.map((item) => ({
        ...(item.variant_group_id
          ? { variant_group_id: item.variant_group_id }
          : { product_id: item.product_id }),
        quantity: parseInt(item.quantity, 10),
      })),
    };

    try {
      if (editing.id) {
        await packsAPI.update(editing.id, payload);
      } else {
        await packsAPI.create(payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("packs.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  // Ranges and products are searched together; a range is "any variant of it"
  const matches = search.trim()
    ? [
        ...ranges
          .filter(
            (range) =>
              !editing?.items.some((item) => item.variant_group_id === range.id) &&
              range.name.toLowerCase().includes(search.trim().toLowerCase()),
          )
          .map((range) => ({ kind: "range", id: range.id, name: range.name })),
        ...products
          .filter(
            (product) =>
              !editing?.items.some((item) => item.product_id === product.id) &&
              product.name.toLowerCase().includes(search.trim().toLowerCase()),
          )
          .map((product) => ({ kind: "product", id: product.id, name: product.name })),
      ].slice(0, 8)
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("packs.title")}
          </h1>
          <p className="text-gray-600">{t("packs.subtitle")}</p>
        </div>
        {can("packs.edit") && (
          <button
            onClick={() =>
              setEditing(
                editing
                  ? null
                  : { name: "", price: "", description: "", items: [] },
              )
            }
            className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
          >
            {editing ? t("common.cancel") : t("packs.new")}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {editing && (
        <form
          onSubmit={save}
          className="bg-white rounded-xl shadow-lg p-6 space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("packs.name")}
              </span>
              <input
                type="text"
                required
                value={editing.name}
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("packs.price", { currency })}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={editing.price}
                onChange={(e) =>
                  setEditing({ ...editing, price: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              {editing.items.length > 0 && editing.price && (
                <span className="text-xs text-gray-500">
                  {t("packs.soldSeparately", {
                    list: money(listValue(editing.items)),
                    saving: money(
                      listValue(editing.items) - parseFloat(editing.price || 0),
                    ),
                  })}
                </span>
              )}
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("packs.contents")}
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("packs.searchProduct")}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            {matches.length > 0 && (
              <ul className="mt-1 border border-gray-200 rounded-lg divide-y divide-gray-100">
                {matches.map((match) => (
                  <li key={`${match.kind}:${match.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing({
                          ...editing,
                          items: [
                            ...editing.items,
                            {
                              ...(match.kind === "range"
                                ? { variant_group_id: match.id }
                                : { product_id: match.id }),
                              name: match.name,
                              quantity: 1,
                            },
                          ],
                        });
                        setSearch("");
                      }}
                      className="w-full text-start px-4 py-2 hover:bg-gray-50"
                    >
                      {match.kind === "range" && (
                        <span className="me-2 text-xs px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">
                          🗂️ {t("packs.anyVariant")}
                        </span>
                      )}
                      {match.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <ul className="mt-3 space-y-2">
              {editing.items.map((item, index) => {
                const name =
                  item.name ||
                  (item.variant_group_id
                    ? ranges.find((r) => r.id === item.variant_group_id)?.name
                    : products.find((p) => p.id === item.product_id)?.name);

                return (
                  <li
                    key={item.variant_group_id || item.product_id}
                    className="flex items-center gap-3"
                  >
                    <span className="flex-1">
                      {item.variant_group_id && (
                        <span className="me-2 text-xs px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">
                          🗂️ {t("packs.anyVariant")}
                        </span>
                      )}
                      {name}
                    </span>
                    <input
                      type="number"
                      min="1"
                      dir="ltr"
                      value={item.quantity}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          items: editing.items.map((line, i) =>
                            i === index
                              ? { ...line, quantity: e.target.value }
                              : line,
                          ),
                        })
                      }
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-right"
                    />
                    <button
                      type="button"
                      aria-label={t("packs.removeItem", { name })}
                      onClick={() =>
                        setEditing({
                          ...editing,
                          items: editing.items.filter((_, i) => i !== index),
                        })
                      }
                      className="text-gray-400 hover:text-red-600"
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ul>
            {editing.items.some((item) => item.variant_group_id) && (
              <p className="mt-2 text-xs text-gray-500">{t("packs.slotHint")}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={busy || editing.items.length === 0}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
          >
            {busy ? t("common.saving") : t("packs.save")}
          </button>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {packs.length === 0 ? (
          <EmptyState screen="packs" message={t("packs.none")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {packs.map((pack) => (
              <li key={pack.id} className="py-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-medium text-gray-900">{pack.name}</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-bold whitespace-nowrap">
                      {money(pack.price)}
                    </span>
                    <span
                      className={`text-sm ${
                        pack.available === 0 ? "text-red-600" : "text-gray-500"
                      }`}
                    >
                      {pack.available === 0
                        ? t("packs.cannotMake")
                        : t("packs.canMake", { count: pack.available })}
                    </span>
                    {can("packs.edit") && (
                      <button
                        onClick={() =>
                          setEditing({
                            ...pack,
                            items: pack.items.map((item) => ({
                              product_id: item.product_id,
                              variant_group_id: item.variant_group_id,
                              name: item.product_name,
                              quantity: item.quantity,
                            })),
                          })
                        }
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                      >
                        {t("common.edit")}
                      </button>
                    )}
                  </div>
                </div>
                {/* Allowed — a loss leader can be deliberate — but never unnoticed */}
                {pack.below_cost && (
                  <p className="mt-1 text-sm font-medium text-amber-700">
                    ⚠️{" "}
                    {t("packs.belowCost", {
                      cost: money(pack.components_cost),
                    })}
                  </p>
                )}
                <ul className="mt-1 text-sm text-gray-500">
                  {pack.items.map((item) => (
                    <li key={item.id}>
                      {item.quantity} × {item.product_name}
                      {item.variant_group_id && (
                        <span className="ms-1 text-primary-600">
                          🗂️ {t("packs.anyVariant")}
                        </span>
                      )}
                      <span className="text-gray-400">
                        {" "}
                        {t("packs.inStock", { count: item.stock_quantity })}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Packs;
